import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import DraftsIcon from "@mui/icons-material/Drafts";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  getWipNotifications,
  triggerMarkWipNotificationAsRead,
  triggerMarkAllWipNotificationsAsRead,
} from "../../reducers/wipNotification.reducer";
import {
  selectWipNotifications,
  selectWipNotificationLoading,
  selectWipNotificationPagination,
} from "../../selectors/wipNotification.selector";
import { selectSupplierId } from "../../selectors/login.selector";
import { formatDate } from "../../utils/dateUtils";

const WIPNotificationsComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notifications = useSelector(selectWipNotifications);
  const loading = useSelector(selectWipNotificationLoading);
  const pagination = useSelector(selectWipNotificationPagination);
  const supplierId = useSelector(selectSupplierId);

  const [localNotifications, setLocalNotifications] = useState([]);

  useEffect(() => {
    dispatch(getWipNotifications({ limit: 50, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  const handleCardClick = (notification) => {
    // Navigate to WIP batch view or detail view based on notification data
    // For supplier users, navigate to batch view or detail view
    if (notification.batchUpdateId) {
      navigate(`/wip/batch?batchId=${notification.batchUpdateId}`);
    } else if (notification.wipItemIds && notification.wipItemIds.length > 0) {
      // Navigate to first WIP item detail
      navigate(`/wip/details?trackerId=${notification.wipItemIds[0]}`);
    } else {
      // Fallback to WIP main page
      navigate("/wip");
    }
  };

  const handleMarkAsRead = (e, notificationId) => {
    e.stopPropagation();
    dispatch(triggerMarkWipNotificationAsRead({ notificationId }));
  };

  const handleMarkAllAsRead = () => {
    dispatch(triggerMarkAllWipNotificationsAsRead());
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      actual_dates_updated: "Actual Dates Updated",
      dates_approved: "Dates Approved",
      dates_rejected: "Dates Rejected",
    };
    return labels[type] || type;
  };

  const getNotificationTypeColor = (type) => {
    const colors = {
      actual_dates_updated: "#1976d2",
      dates_approved: "#2e7d32",
      dates_rejected: "#d32f2f",
    };
    return colors[type] || "#666";
  };

  // Filter notifications based on user type (supplier vs brand)
  // Supplier users should only see notifications where senderType is 'brand'
  // Brand users should only see notifications where senderType is 'supplier'
  const filteredNotifications = localNotifications.filter((notification) => {
    if (supplierId) {
      // User is a supplier - show brand notifications
      return notification.senderType === "brand";
    } else {
      // User is a brand - show supplier notifications
      return notification.senderType === "supplier";
    }
  });

  if (loading && filteredNotifications.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(95vh - 120px)",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading WIP notifications...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "calc(95vh - 120px)",
        overflowY: "auto",
        pr: 2,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      {filteredNotifications.length > 0 && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={handleMarkAllAsRead}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            Mark All as Read
          </Button>
        </Box>
      )}

      {filteredNotifications.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No WIP notifications
          </Typography>
        </Box>
      ) : (
        filteredNotifications.map((notification) => (
          <Card
            key={notification._id}
            sx={{
              mb: 2,
              boxShadow:
                "0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1)",
              border: "1px solid rgba(234, 236, 240, 1)",
              borderRadius: "12px",
              cursor: "pointer",
              opacity: notification.isRead ? 0.7 : 1,
              "&:hover": {
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              },
            }}
            onClick={() => handleCardClick(notification)}
          >
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="start"
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      backgroundColor: "#F2F4F7",
                      p: 1,
                      borderRadius: "12px",
                      display: "inline-block",
                    }}
                  >
                    <span
                      style={{
                        color: getNotificationTypeColor(
                          notification.notificationType
                        ),
                        fontWeight: 600,
                      }}
                    >
                      {getNotificationTypeLabel(notification.notificationType)}
                    </span>
                    {notification.metadata?.itemCount && (
                      <span style={{ marginLeft: "8px", color: "#666" }}>
                        • {notification.metadata.itemCount} item
                        {notification.metadata.itemCount !== 1 ? "s" : ""}
                      </span>
                    )}
                  </Typography>
                  <Typography
                    sx={{ mt: 1, pl: 1, fontWeight: 600, fontSize: "16px" }}
                  >
                    {notification.subject}
                  </Typography>
                  <Typography
                    sx={{ mt: 1, pl: 1, fontWeight: 400, fontSize: "14px" }}
                  >
                    {notification.message}
                  </Typography>
                  <Typography
                    sx={{ mt: 1, fontSize: "8px", textAlign: "right" }}
                    color="text.secondary"
                  >
                    {notification.senderType === "supplier"
                      ? "From Supplier"
                      : "From Brand"}
                    <br />
                    {formatDate(notification.createdAt)}
                  </Typography>
                </Box>
                {!notification.isRead && (
                  <IconButton
                    onClick={(e) => handleMarkAsRead(e, notification._id)}
                    sx={{ ml: 1 }}
                  >
                    <Tooltip title="Mark as read">
                      <MarkEmailUnreadIcon />
                    </Tooltip>
                  </IconButton>
                )}
                {notification.isRead && (
                  <IconButton
                    onClick={(e) => handleMarkAsRead(e, notification._id)}
                    sx={{ ml: 1 }}
                  >
                    <Tooltip title="Mark as unread">
                      <DraftsIcon sx={{ color: "#1976d2" }} />
                    </Tooltip>
                  </IconButton>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );
};

export default WIPNotificationsComponent;

